// Elytra glide flight (M21 — The End). PURE (ENG-1).
//
// The End's signature reward: gliding flight. While a player wearing elytra in
// the chest slot is airborne and gliding, the look direction drives the physics —
// pitch down trades altitude for speed, pitch up trades speed for altitude, and a
// firework rocket adds a damageless forward boost along the look vector. This
// mirrors LivingEntity's fall-flying branch (Minecraft 1.21): lift from the
// squared cosine of pitch, a dive→speed exchange, an ascent→lift exchange, and a
// steer that turns horizontal motion toward the look heading. Velocity here is in
// blocks/tick, matching engine/physics.ts; the host applies collision afterward.
import { ELYTRA, PLAYER } from '../config';

/** Gravity magnitude (PLAYER.GRAVITY is stored signed, −0.08). */
const GRAVITY = -PLAYER.GRAVITY;

/** The chest-slot item that grants gliding flight. */
export const ELYTRA_ITEM = 'elytra';

/** How long one firework rocket boosts a glide (ticks); the boost is damageless. */
export const FIREWORK_BOOST_TICKS = ELYTRA.FIREWORK_BOOST_TICKS;

export interface Vel3 {
  vx: number;
  vy: number;
  vz: number;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export function isElytra(item: string | null | undefined): boolean {
  return item === ELYTRA_ITEM;
}

/** Unit look direction in the physics yaw/pitch convention: forward at pitch 0 is
 *  (−sin yaw, 0, −cos yaw), and pitch > 0 looks up (y = sin pitch). */
export function lookVector(yaw: number, pitch: number): Vec3 {
  const cp = Math.cos(pitch);
  return { x: -Math.sin(yaw) * cp, y: Math.sin(pitch), z: -Math.cos(yaw) * cp };
}

/** May the player begin gliding right now? MC starts fall-flying on a jump press
 *  while airborne, wearing elytra, out of fluid, and not creative-flying. */
export function canStartGliding(o: {
  hasElytra: boolean;
  onGround: boolean;
  inFluid: boolean;
  creativeFlying: boolean;
}): boolean {
  return o.hasElytra && !o.onGround && !o.inFluid && !o.creativeFlying;
}

/** Gliding ends when you touch the ground, enter a fluid, or lose the elytra. */
export function shouldStopGliding(o: {
  hasElytra: boolean;
  onGround: boolean;
  inFluid: boolean;
}): boolean {
  return !o.hasElytra || o.onGround || o.inFluid;
}

/** One tick of elytra aerodynamics: transform velocity (blocks/tick) given the
 *  look direction, returning the new velocity. The host then collides + moves.
 *  Faithful to Minecraft 1.21's fall-flying travel branch. */
export function elytraAerodynamics(v: Vel3, yaw: number, pitch: number): Vel3 {
  const look = lookVector(yaw, pitch);
  const hl = Math.hypot(look.x, look.z); // cos(pitch): horizontal look magnitude
  const horizSpeed = Math.hypot(v.vx, v.vz);
  let { vx, vy, vz } = v;

  // Lift: the squared cosine of pitch offsets gravity — level flight sinks gently
  // (~¼ g), a steep dive gives up nearly all lift.
  const cos2 = hl * hl; // = cos(pitch)^2 (the look vector is unit length)
  vy += GRAVITY * (-1 + cos2 * ELYTRA.LIFT);

  // Diving trades descent for forward speed, along the look heading.
  if (vy < 0 && hl > 0) {
    const gain = vy * -ELYTRA.DIVE_GAIN * cos2;
    vy += gain;
    vx += (look.x / hl) * gain;
    vz += (look.z / hl) * gain;
  }
  // Climbing (looking up) trades forward speed for altitude.
  if (pitch > 0 && hl > 0) {
    const climb = horizSpeed * Math.sin(pitch) * ELYTRA.CLIMB_RATE;
    vy += climb * ELYTRA.CLIMB_LIFT;
    vx -= (look.x / hl) * climb;
    vz -= (look.z / hl) * climb;
  }
  // Steer horizontal motion toward the look heading (turns as you turn).
  if (hl > 0) {
    vx += ((look.x / hl) * horizSpeed - vx) * ELYTRA.STEER;
    vz += ((look.z / hl) * horizSpeed - vz) * ELYTRA.STEER;
  }
  // Aerodynamic drag.
  vx *= ELYTRA.H_DRAG;
  vy *= ELYTRA.V_DRAG;
  vz *= ELYTRA.H_DRAG;
  return { vx, vy, vz };
}

/** A firework rocket boost while gliding: accelerate toward the look vector. A
 *  plain rocket carries no explosion, so the boost never deals damage (MC's
 *  per-tick push while the rocket is lit). */
export function fireworkBoost(v: Vel3, yaw: number, pitch: number): Vel3 {
  const look = lookVector(yaw, pitch);
  return {
    vx: v.vx + look.x * 0.1 + (look.x * 1.5 - v.vx) * 0.5,
    vy: v.vy + look.y * 0.1 + (look.y * 1.5 - v.vy) * 0.5,
    vz: v.vz + look.z * 0.1 + (look.z * 1.5 - v.vz) * 0.5,
  };
}
